import { useMemo } from "react";

import { usePermissions, useRbacConfig } from "@/utils/casl";

/**
 * Hook for checking specific permissions
 * Provides convenient methods for common permission checks
 */
export function usePermissionCheck() {
  const { can, cannot } = usePermissions();
  const rbacConfig = useRbacConfig();

  const permissions = useMemo(
    () => ({
      // Lead permissions
      canCreateLead: () =>
        can(
          rbacConfig.actions.CREATE as string,
          rbacConfig.subjects.LEAD as string,
        ),
      canReadLead: () =>
        can(
          rbacConfig.actions.READ as string,
          rbacConfig.subjects.LEAD as string,
        ),
      canUpdateLead: () =>
        can(
          rbacConfig.actions.UPDATE as string,
          rbacConfig.subjects.LEAD as string,
        ),
      canListLeads: () =>
        can(
          rbacConfig.actions.LIST as string,
          rbacConfig.subjects.LEAD as string,
        ),

      // User permissions
      canCreateUser: () =>
        can(
          rbacConfig.actions.CREATE as string,
          rbacConfig.subjects.USER as string,
        ),
      canReadUser: () =>
        can(
          rbacConfig.actions.READ as string,
          rbacConfig.subjects.USER as string,
        ),
      canUpdateUser: () =>
        can(
          rbacConfig.actions.UPDATE as string,
          rbacConfig.subjects.USER as string,
        ),
      canListUsers: () =>
        can(
          rbacConfig.actions.LIST as string,
          rbacConfig.subjects.USER as string,
        ),

      // Master permissions
      canReadMaster: () =>
        can(
          rbacConfig.actions.READ as string,
          rbacConfig.subjects.MASTER as string,
        ),

      // LDAP permissions
      canReadLdap: () =>
        can(
          rbacConfig.actions.READ as string,
          rbacConfig.subjects.LDAP as string,
        ),

      // User Selection permissions
      canCreateUserSelection: () =>
        can(
          rbacConfig.actions.CREATE as string,
          rbacConfig.subjects.USER_SELECTION as string,
        ),

      // OTP permissions
      canCreateOtp: () =>
        can(
          rbacConfig.actions.CREATE as string,
          rbacConfig.subjects.OTP as string,
        ),
      canUpdateOtp: () =>
        can(
          rbacConfig.actions.UPDATE as string,
          rbacConfig.subjects.OTP as string,
        ),

      // User Document permissions
      canCreateUserDocument: () =>
        can(
          rbacConfig.actions.CREATE as string,
          rbacConfig.subjects.USER_DOCUMENT as string,
        ),
      canDeleteUserDocument: () =>
        can(
          rbacConfig.actions.DELETE as string,
          rbacConfig.subjects.USER_DOCUMENT as string,
        ),

      // Generic permission check
      canPerform: (action: string, subject: string) => can(action, subject),
      cannotPerform: (action: string, subject: string) =>
        cannot(action, subject),
    }),
    [can, cannot, rbacConfig],
  );

  return permissions;
}
